import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { GraduationCapIcon, CalendarIcon, ClockIcon, MapPinIcon, BookOpenIcon, BuildingIcon, AwardIcon, ArrowLeftIcon, ShareIcon, ChevronRightIcon, BrainIcon, LightbulbIcon, ZapIcon, SparklesIcon, TagIcon, CheckIcon, ExternalLinkIcon, HeartIcon, UsersIcon, BriefcaseIcon, CheckCircleIcon, TrendingUpIcon, DollarSignIcon, FileTextIcon, StarIcon, InfoIcon, GlobeIcon } from 'lucide-react';
// Sample program data - in a real app, this would come from an API
const samplePrograms = [{
  id: 'PRG001',
  name: 'Master of Business Administration',
  type: "Master's",
  duration: '2 Years',
  status: 'Active',
  applications: 48,
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  matchScore: 92,
  universityLogo: 'https://placehold.co/100x100?text=UBS',
  university: 'UAE Business School',
  location: 'Dubai, UAE',
  description: 'Our MBA program is designed to develop the skills required for careers in business and management. The program integrates theoretical and practical knowledge of business with an international perspective.',
  learningOutcomes: ['Demonstrate advanced knowledge of business concepts and theories', 'Apply strategic thinking to complex business problems', 'Develop leadership skills for diverse business environments', 'Analyze financial data to make informed business decisions', 'Implement effective organizational change strategies', 'Understand global business practices and cross-cultural management'],
  targetAudience: ['Business Professionals', 'Career Changers', 'Recent Graduates', 'Entrepreneurs'],
  deliveryMode: 'Hybrid',
  tuition: '120,000',
  qualifications: ["Bachelor's degree with a minimum GPA of 3.0", '2+ years of professional work experience', 'English proficiency (IELTS 6.5 or equivalent)', 'Two professional references'],
  accreditation: 'Fully accredited by the Ministry of Education and the Association to Advance Collegiate Schools of Business (AACSB)',
  campusLocation: 'Dubai Main Campus',
  skills: [{
    name: 'Strategic Management',
    category: 'Technical',
    confidence: 0.95
  }, {
    name: 'Financial Analysis',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Business Intelligence',
    category: 'Technical',
    confidence: 0.89
  }, {
    name: 'Leadership',
    category: 'Soft',
    confidence: 0.94
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.91
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.9
  }, {
    name: 'Communication',
    category: 'Soft',
    confidence: 0.88
  }, {
    name: 'Business Administration',
    category: 'Industry',
    confidence: 0.93
  }, {
    name: 'Corporate Finance',
    category: 'Industry',
    confidence: 0.87
  }, {
    name: 'Management Consulting',
    category: 'Industry',
    confidence: 0.85
  }],
  topics: ['Business Strategy', 'Financial Management', 'Marketing', 'Organizational Behavior', 'Global Business', 'Leadership'],
  labels: ['High Demand', 'High Salary', 'International Recognition'],
  averageSalary: '320,000 AED',
  employmentRate: '94%',
  applicationDeadline: 'June 30, 2023',
  nextIntake: 'September 2023',
  universityWebsite: 'https://www.ubsdubai.ac.ae',
  universityDescription: 'UAE Business School is a leading institution for business education in the Middle East, with a focus on developing future leaders with global perspectives and local insights.',
  facultyMembers: [{
    name: 'Dr. Ahmed Al Mansouri',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/men/1.jpg'
  }, {
    name: 'Dr. Fatima Al Zaabi',
    title: 'Professor of Finance',
    image: 'https://randomuser.me/api/portraits/women/2.jpg'
  }, {
    name: 'Dr. Mohammed Al Hashimi',
    title: 'Professor of Marketing',
    image: 'https://randomuser.me/api/portraits/men/3.jpg'
  }],
  careerOpportunities: ['Business Consultant', 'Financial Analyst', 'Marketing Manager', 'Operations Director', 'Entrepreneur', 'Project Manager'],
  programStructure: [{
    semester: 'Semester 1',
    courses: ['Business Ethics', 'Financial Accounting', 'Marketing Management', 'Organizational Behavior']
  }, {
    semester: 'Semester 2',
    courses: ['Corporate Finance', 'Operations Management', 'Strategic Management', 'Business Analytics']
  }, {
    semester: 'Semester 3',
    courses: ['International Business', 'Leadership and Change', 'Elective 1', 'Elective 2']
  }, {
    semester: 'Semester 4',
    courses: ['Capstone Project', 'Business Consulting Project', 'Elective 3', 'Elective 4']
  }],
  testimonials: [{
    quote: 'The MBA program provided me with both theoretical knowledge and practical skills that were immediately applicable in my career.',
    name: 'Sara Ahmed',
    title: 'Finance Manager, Dubai Holdings',
    image: 'https://randomuser.me/api/portraits/women/65.jpg'
  }, {
    quote: 'The professors bring real-world experience into the classroom, making complex business concepts accessible and relevant.',
    name: 'Omar Al Suwaidi',
    title: 'Senior Consultant, KPMG',
    image: 'https://randomuser.me/api/portraits/men/32.jpg'
  }],
  faqs: [{
    question: 'Can I study this program part-time while working?',
    answer: 'Yes, our MBA program is designed to accommodate working professionals. Classes are scheduled in the evenings and weekends, and the hybrid format allows for some online learning.'
  }, {
    question: 'Are there scholarships available for this program?',
    answer: 'Yes, we offer merit-based scholarships that cover up to 50% of the tuition fees. Additionally, there are specific scholarships for UAE nationals and women in business.'
  }, {
    question: 'What is the application deadline?',
    answer: 'For Fall 2023 intake, the application deadline is June 30, 2023. For Spring 2024, the deadline is November 30, 2023.'
  }],
  requiredDocuments: ['Official academic transcripts', 'CV/Resume', 'Personal statement', 'Two letters of recommendation', 'English proficiency test results', 'Copy of passport or ID']
}, {
  id: 'PRG002',
  name: 'Bachelor of Computer Science',
  type: "Bachelor's",
  duration: '4 Years',
  status: 'Active',
  applications: 112,
  intakePeriods: ['Fall 2023'],
  matchScore: 88,
  universityLogo: 'https://placehold.co/100x100?text=UTI',
  university: 'UAE Technology Institute',
  location: 'Abu Dhabi, UAE',
  description: 'The Bachelor of Computer Science program provides students with a comprehensive foundation in computer science principles, programming languages, and software development methodologies. Students will develop technical skills and problem-solving abilities necessary for careers in the rapidly evolving technology sector.',
  learningOutcomes: ['Design and implement efficient algorithms and data structures', 'Develop software using various programming languages and frameworks', 'Apply database design principles and management systems', 'Understand computer networks and security protocols', 'Implement artificial intelligence and machine learning concepts', 'Collaborate effectively in software development teams'],
  targetAudience: ['High School Graduates', 'Technology Enthusiasts', 'Career Changers'],
  deliveryMode: 'In-Person',
  tuition: '80,000',
  qualifications: ['High school diploma with strong performance in mathematics', 'Minimum GPA of 3.0 or equivalent', 'English proficiency (IELTS 6.0 or equivalent)', 'Basic computer literacy'],
  accreditation: 'Accredited by the Ministry of Education and the Computing Accreditation Commission of ABET',
  campusLocation: 'Abu Dhabi Main Campus',
  skills: [{
    name: 'Programming',
    category: 'Technical',
    confidence: 0.96
  }, {
    name: 'Data Structures',
    category: 'Technical',
    confidence: 0.94
  }, {
    name: 'Algorithms',
    category: 'Technical',
    confidence: 0.92
  }, {
    name: 'Database Management',
    category: 'Technical',
    confidence: 0.9
  }, {
    name: 'Problem Solving',
    category: 'Soft',
    confidence: 0.95
  }, {
    name: 'Critical Thinking',
    category: 'Soft',
    confidence: 0.93
  }, {
    name: 'Teamwork',
    category: 'Soft',
    confidence: 0.85
  }, {
    name: 'Software Development',
    category: 'Industry',
    confidence: 0.96
  }, {
    name: 'Web Development',
    category: 'Industry',
    confidence: 0.88
  }, {
    name: 'Mobile App Development',
    category: 'Industry',
    confidence: 0.86
  }],
  topics: ['Programming Fundamentals', 'Data Structures & Algorithms', 'Database Systems', 'Web Development', 'Software Engineering', 'Artificial Intelligence'],
  labels: ['High Demand', 'High Salary', 'Fast Completion'],
  averageSalary: '280,000 AED',
  employmentRate: '92%',
  applicationDeadline: 'April 30, 2024',
  nextIntake: 'September 2024',
  universityWebsite: 'https://www.uaeti.ac.ae',
  universityDescription: 'UAE Technology Institute is a premier institution focused on technology education and innovation, preparing students for leadership roles in the digital economy.',
  facultyMembers: [{
    name: 'Dr. Ali Hassan',
    title: 'Program Director',
    image: 'https://randomuser.me/api/portraits/men/4.jpg'
  }, {
    name: 'Dr. Layla Mahmoud',
    title: 'Professor of AI',
    image: 'https://randomuser.me/api/portraits/women/5.jpg'
  }, {
    name: 'Dr. Khalid Al Mazrouei',
    title: 'Professor of Software Engineering',
    image: 'https://randomuser.me/api/portraits/men/6.jpg'
  }],
  careerOpportunities: ['Software Developer', 'Web Developer', 'Mobile App Developer', 'Database Administrator', 'Systems Analyst', 'IT Consultant'],
  programStructure: [{
    semester: 'Year 1',
    courses: ['Introduction to Programming', 'Discrete Mathematics', 'Computer Systems', 'Calculus', 'Academic English']
  }, {
    semester: 'Year 2',
    courses: ['Data Structures and Algorithms', 'Object-Oriented Programming', 'Database Systems', 'Computer Networks', 'Professional Communication']
  }, {
    semester: 'Year 3',
    courses: ['Software Engineering', 'Operating Systems', 'Web Development', 'Artificial Intelligence', 'Cybersecurity']
  }, {
    semester: 'Year 4',
    courses: ['Capstone Project', 'Machine Learning', 'Cloud Computing', 'Elective 1', 'Elective 2']
  }],
  testimonials: [{
    quote: 'The hands-on approach to learning and industry-relevant curriculum prepared me for a successful career in software development.',
    name: 'Reem Al Falasi',
    title: 'Software Engineer, Microsoft',
    image: 'https://randomuser.me/api/portraits/women/45.jpg'
  }, {
    quote: "The faculty's expertise and mentorship were invaluable in helping me develop both technical skills and professional confidence.",
    name: 'Saif Al Shamsi',
    title: 'Full Stack Developer, Amazon',
    image: 'https://randomuser.me/api/portraits/men/22.jpg'
  }],
  faqs: [{
    question: 'Is prior programming experience required?',
    answer: 'No, the program is designed to accommodate students with no prior programming experience. We start with the fundamentals and progressively build more advanced skills.'
  }, {
    question: 'What kind of computers or equipment do I need?',
    answer: 'Students are required to have a laptop with at least 8GB RAM and an i5 processor or equivalent. The university provides access to specialized software and computing resources.'
  }, {
    question: 'Are there internship opportunities?',
    answer: 'Yes, we have partnerships with leading technology companies in the UAE and internationally. Students typically complete an internship during the summer after their third year.'
  }],
  requiredDocuments: ['High school transcripts', 'Standardized test scores (if applicable)', 'English proficiency test results', 'Personal statement', 'Two letters of recommendation', 'Copy of passport or ID']
}
// Additional programs would be here
];
// Skill badge component
const SkillBadge = ({
  name,
  category,
  confidence
}) => {
  const getCategoryColor = () => {
    switch (category) {
      case 'Technical':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Soft':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Industry':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border ${getCategoryColor()}`} title={`Confidence: ${Math.round(confidence * 100)}%`} data-id="element-4696">
      {name}
      {confidence >= 0.9 && <span className="ml-1.5 flex-shrink-0" data-id="element-4697">
          <CheckIcon className="w-3 h-3 text-green-600" data-id="element-4698" />
        </span>}
    </span>;
};
// Topic badge component
const TopicBadge = ({
  name
}) => <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm border bg-gray-100 text-gray-800 border-gray-200" data-id="element-4699">
    {name}
  </span>;
// Label badge component
const LabelBadge = ({
  name
}) => {
  const getLabelColor = () => {
    switch (name) {
      case 'High Demand':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'High Salary':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'International Recognition':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Fast Completion':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-primary-100 text-primary-800 border-primary-200';
    }
  };
  const getLabelIcon = () => {
    switch (name) {
      case 'High Demand':
        return <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-4700" />;
      case 'High Salary':
        return <DollarSignIcon className="w-3 h-3 mr-1" data-id="element-4701" />;
      case 'International Recognition':
        return <GlobeIcon className="w-3 h-3 mr-1" data-id="element-4702" />;
      case 'Fast Completion':
        return <ClockIcon className="w-3 h-3 mr-1" data-id="element-4703" />;
      default:
        return <TagIcon className="w-3 h-3 mr-1" data-id="element-4704" />;
    }
  };
  return <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border ${getLabelColor()}`} data-id="element-4705">
      {getLabelIcon()}
      {name}
    </span>;
};
// FAQ Accordion Item
const FAQItem = ({
  question,
  answer
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-b-0" data-id="element-4706">
      <button className="flex justify-between items-center w-full py-4 text-left font-medium text-gray-900 hover:text-primary-600 focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-4707">
        <span data-id="element-4708">{question}</span>
        <ChevronRightIcon className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'transform rotate-90' : ''}`} data-id="element-4709" />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'}`} data-id="element-4710">
        <p className="text-gray-600" data-id="element-4711">{answer}</p>
      </div>
    </div>;
};
// Testimonial Card
const TestimonialCard = ({
  quote,
  name,
  title,
  image
}) => <div className="bg-gray-50 rounded-lg p-5 border border-gray-100" data-id="element-4712">
    <div className="flex items-start mb-4" data-id="element-4713">
      <div className="flex-shrink-0 mr-4" data-id="element-4714">
        <img src={image} alt={name} className="h-12 w-12 rounded-full object-cover" data-id="element-4715" />
      </div>
      <div data-id="element-4716">
        <p className="text-gray-600 italic mb-3" data-id="element-4717">"{quote}"</p>
        <div data-id="element-4718">
          <p className="font-medium text-gray-800" data-id="element-4719">{name}</p>
          <p className="text-sm text-gray-500" data-id="element-4720">{title}</p>
        </div>
      </div>
    </div>
  </div>;
// Program Structure Accordion
const ProgramStructureAccordion = ({
  structure
}) => {
  const [openSemester, setOpenSemester] = useState(null);
  return <div className="space-y-3" data-id="element-4721">
      {structure.map((semester, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4722">
          <button className="w-full flex justify-between items-center p-4 bg-gray-50 text-left font-medium text-gray-800 hover:bg-gray-100" onClick={() => setOpenSemester(openSemester === index ? null : index)} data-id="element-4723">
            <span data-id="element-4724">{semester.semester}</span>
            <ChevronRightIcon className={`w-5 h-5 text-gray-500 transition-transform ${openSemester === index ? 'transform rotate-90' : ''}`} data-id="element-4725" />
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${openSemester === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-4726">
            <div className="p-4 bg-white" data-id="element-4727">
              <ul className="space-y-2" data-id="element-4728">
                {semester.courses.map((course, courseIndex) => <li key={courseIndex} className="flex items-center" data-id="element-4729">
                    <div className="w-2 h-2 rounded-full bg-primary-500 mr-3" data-id="element-4730"></div>
                    <span className="text-gray-700" data-id="element-4731">{course}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>)}
    </div>;
};
const StudentProgramDetail = () => {
  const {
    programId
  } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchProgram = () => {
      setLoading(true);
      // For testing purposes, if no programId is provided or it's invalid,
      // default to the first program in the array
      let foundProgram = null;
      if (programId) {
        // Try to find the program with the matching ID
        foundProgram = samplePrograms.find(p => p.id === programId);
      }
      // If no program is found with that ID, or no ID was provided, use the first program
      if (!foundProgram && samplePrograms.length > 0) {
        foundProgram = samplePrograms[0];
        console.log('Using default program:', foundProgram.id);
      }
      setProgram(foundProgram);
      setLoading(false);
    };
    fetchProgram();
    // Add scroll listener for sticky header
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setHeaderVisible(scrollPosition > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [programId]);
  const toggleSave = () => {
    setIsSaved(!isSaved);
    // In a real app, you would save this to the user's profile
  };
  const handleShare = () => {
    // In a real app, this would open a share dialog
    if (navigator.share) {
      navigator.share({
        title: program.name,
        text: `Check out this program: ${program.name} at ${program.university}`,
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support the Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  const handleApply = () => {
    // Navigate to the application form with the program ID
    navigate(`/student/application/${programId}`);
  };
  if (loading) {
    return <Layout persona="Student" data-id="element-4732">
        <div className="flex justify-center items-center h-64" data-id="element-4733">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" data-id="element-4734"></div>
        </div>
      </Layout>;
  }
  if (!program) {
    return <Layout persona="Student" data-id="element-4735">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center" data-id="element-4736">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-4737">
            <GraduationCapIcon className="w-8 h-8 text-gray-400" data-id="element-4738" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-4739">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-4740">
            The program you're looking for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/student/programs')} className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors" data-id="element-4741">
            Back to Programs
          </button>
        </div>
      </Layout>;
  }
  return <Layout persona="Student" data-id="element-4742">
      {/* Sticky apply button - appears on scroll */}
      {headerVisible && <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg p-4 md:hidden" data-id="element-4743">
          <div className="flex justify-between items-center" data-id="element-4744">
            <div className="flex-1 mr-4" data-id="element-4745">
              <p className="font-medium text-gray-900 text-sm truncate" data-id="element-4746">
                {program.name}
              </p>
              <p className="text-sm text-gray-500" data-id="element-4747">{program.university}</p>
            </div>
            <button onClick={handleApply} className="bg-primary-500 text-white px-5 py-2.5 rounded-md font-medium hover:bg-primary-600 transition-colors flex-shrink-0" data-id="element-4748">
              Apply Now
            </button>
          </div>
        </div>}
      {/* Back button */}

      {/* Program Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8" data-id="element-4749">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-10 md:px-8 md:py-12 text-white" data-id="element-4750">
          <div className="max-w-5xl mx-auto" data-id="element-4751">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between" data-id="element-4752">
              <div className="w-full md:w-2/3 mb-6 md:mb-0 md:pr-8" data-id="element-4753">
                {program.matchScore && <div className="inline-flex items-center px-3 py-1 bg-yellow-400 text-gray-900 text-sm font-medium rounded-full mb-4" data-id="element-4754">
                    <SparklesIcon className="w-4 h-4 mr-1.5" data-id="element-4755" />
                    {program.matchScore}% Match for Your Profile
                  </div>}
                <h1 className="text-2xl md:text-4xl font-bold mb-3" data-id="element-4756">
                  {program.name}
                </h1>
                <div className="flex items-center mb-6" data-id="element-4757">
                  {program.universityLogo ? <img src={program.universityLogo} alt={program.university} className="h-8 w-8 rounded-full bg-white p-1 mr-2" data-id="element-4758" /> : <BuildingIcon className="h-6 w-6 mr-2 text-white/90" data-id="element-4759" />}
                  <span className="text-lg text-white/90" data-id="element-4760">
                    {program.university}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3" data-id="element-4761">
                  <span className="inline-flex items-center px-3 py-1.5 bg-white/20 rounded-full text-sm" data-id="element-4762">
                    <GraduationCapIcon className="w-4 h-4 mr-1.5" data-id="element-4763" />
                    {program.type}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 bg-white/20 rounded-full text-sm" data-id="element-4764">
                    <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-4765" />
                    {program.duration}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 bg-white/20 rounded-full text-sm" data-id="element-4766">
                    <MapPinIcon className="w-4 h-4 mr-1.5" data-id="element-4767" />
                    {program.location || program.campusLocation}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 bg-white/20 rounded-full text-sm" data-id="element-4768">
                    <BookOpenIcon className="w-4 h-4 mr-1.5" data-id="element-4769" />
                    {program.deliveryMode}
                  </span>
                </div>
              </div>
              <div className="w-full md:w-1/3 md:pl-4" data-id="element-4770">
                <div className="bg-white rounded-lg p-6 text-gray-900 shadow-lg" data-id="element-4771">
                  <div className="flex justify-between items-center mb-2" data-id="element-4772">
                    <h3 className="font-semibold text-lg" data-id="element-4773">Program Actions</h3>
                    <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-4774" />
                  </div>
                  <div className="text-sm text-gray-600 mb-4" data-id="element-4775">
                    <div className="flex items-center mb-2" data-id="element-4776">
                      <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-4777" />
                      <span data-id="element-4778">
                        Application Deadline:{' '}
                        <strong data-id="element-4779">{program.applicationDeadline}</strong>
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-4780">
                      <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-4781" />
                      <span data-id="element-4782">
                        Next Intake: <strong data-id="element-4783">{program.nextIntake}</strong>
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-4784">
                    <button onClick={handleApply} className="w-full bg-primary-500 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors flex items-center justify-center" data-id="element-4785">
                      Apply Now
                    </button>
                    <div className="flex gap-3" data-id="element-4786">
                      <button onClick={toggleSave} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center
                          ${isSaved ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-4787">
                        <HeartIcon className={`w-4 h-4 mr-2 ${isSaved ? 'fill-red-500' : ''}`} data-id="element-4788" />
                        {isSaved ? 'Saved' : 'Save'}
                      </button>
                      <button onClick={handleShare} className="flex-1 bg-white text-gray-700 border border-gray-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center" data-id="element-4789">
                        <ShareIcon className="w-4 h-4 mr-2" data-id="element-4790" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Quick navigation */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 md:px-8 py-4 overflow-x-auto" data-id="element-4791">
          <div className="max-w-5xl mx-auto" data-id="element-4792">
            <div className="flex space-x-6" data-id="element-4793">
              <a href="#overview" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4794">
                Overview
              </a>
              <a href="#outcomes" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4795">
                Outcomes
              </a>
              <a href="#skills" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4796">
                Skills & Topics
              </a>
              <a href="#structure" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4797">
                Program Structure
              </a>
              <a href="#admission" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4798">
                Admission
              </a>
              <a href="#university" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4799">
                University
              </a>
              <a href="#faqs" className="text-sm font-medium text-gray-700 hover:text-primary-500 whitespace-nowrap" data-id="element-4800">
                FAQs
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto" data-id="element-4801">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4802">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8" data-id="element-4803">
            {/* Program Overview */}
            <section id="overview" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4804">
              <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-4805">
                Program Overview
              </h2>
              <p className="text-gray-700 mb-6" data-id="element-4806">{program.description}</p>
              {/* What You'll Learn */}
              <div className="mb-6" data-id="element-4807">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4808">
                  <BrainIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4809" />
                  What You'll Learn
                </h3>
                <ul className="space-y-2" data-id="element-4810">
                  {program.learningOutcomes.map((outcome, index) => <li key={index} className="flex items-start" data-id="element-4811">
                      <CheckCircleIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-4812" />
                      <span className="text-gray-700" data-id="element-4813">{outcome}</span>
                    </li>)}
                </ul>
              </div>
              {/* AI Personalized Insight */}
              {program.matchScore && program.matchScore > 80 && <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg mb-6" data-id="element-4814">
                  <div className="flex items-start" data-id="element-4815">
                    <SparklesIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4816" />
                    <div data-id="element-4817">
                      <h3 className="font-medium text-blue-800 mb-1" data-id="element-4818">
                        Why This Program Matches Your Profile
                      </h3>
                      <p className="text-sm text-blue-700" data-id="element-4819">
                        Based on your background in {program.skills[0].name} and
                        interest in {program.skills[1].name}, this program
                        aligns well with your career goals. The{' '}
                        {program.deliveryMode} format also matches your
                        preference for flexibility.
                      </p>
                    </div>
                  </div>
                </div>}
              {/* Target Audience */}
              {program.targetAudience && program.targetAudience.length > 0 && <div className="mb-6" data-id="element-4820">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4821">
                    <UsersIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4822" />
                    Who This Program Is For
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-4823">
                    {program.targetAudience.map((audience, index) => <span key={index} className="bg-primary-50 text-primary-700 px-3 py-1.5 rounded-full text-sm" data-id="element-4824">
                        {audience}
                      </span>)}
                  </div>
                </div>}
              {/* Key Program Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4825">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-4826">
                  <div className="flex items-center mb-2" data-id="element-4827">
                    <BookOpenIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4828" />
                    <h3 className="font-medium text-gray-800" data-id="element-4829">Delivery Mode</h3>
                  </div>
                  <p className="text-gray-600" data-id="element-4830">{program.deliveryMode}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-4831">
                  <div className="flex items-center mb-2" data-id="element-4832">
                    <ClockIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4833" />
                    <h3 className="font-medium text-gray-800" data-id="element-4834">Duration</h3>
                  </div>
                  <p className="text-gray-600" data-id="element-4835">{program.duration}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-4836">
                  <div className="flex items-center mb-2" data-id="element-4837">
                    <AwardIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4838" />
                    <h3 className="font-medium text-gray-800" data-id="element-4839">Accreditation</h3>
                  </div>
                  <p className="text-gray-600" data-id="element-4840">{program.accreditation}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-4841">
                  <div className="flex items-center mb-2" data-id="element-4842">
                    <MapPinIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4843" />
                    <h3 className="font-medium text-gray-800" data-id="element-4844">Location</h3>
                  </div>
                  <p className="text-gray-600" data-id="element-4845">{program.campusLocation}</p>
                </div>
              </div>
            </section>
            {/* Career Outcomes */}
            <section id="outcomes" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4846">
              <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-4847">
                Career Outcomes
              </h2>
              {/* Career Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-4848">
                {program.employmentRate && <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center" data-id="element-4849">
                    <p className="text-2xl font-bold text-primary-600" data-id="element-4850">
                      {program.employmentRate}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-4851">Employment Rate</p>
                  </div>}
                {program.averageSalary && <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center" data-id="element-4852">
                    <p className="text-2xl font-bold text-primary-600" data-id="element-4853">
                      {program.averageSalary}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-4854">
                      Average Annual Salary
                    </p>
                  </div>}
              </div>
              {/* Career Opportunities */}
              {program.careerOpportunities && program.careerOpportunities.length > 0 && <div data-id="element-4855">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4856">
                      <BriefcaseIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4857" />
                      Career Opportunities
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-4858">
                      {program.careerOpportunities.map((career, index) => <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-4859">
                          <TagIcon className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" data-id="element-4860" />
                          <span className="text-gray-700" data-id="element-4861">{career}</span>
                        </div>)}
                    </div>
                  </div>}
              {/* Testimonials */}
              {program.testimonials && program.testimonials.length > 0 && <div data-id="element-4862">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4863">
                    <StarIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4864" />
                    Graduate Testimonials
                  </h3>
                  <div className="space-y-4" data-id="element-4865">
                    {program.testimonials.map((testimonial, index) => <TestimonialCard key={index} {...testimonial} data-id="element-4866" />)}
                  </div>
                </div>}
            </section>
            {/* Skills & Topics */}
            <section id="skills" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4867">
              <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-4868">
                Skills & Topics
              </h2>
              <div className="space-y-6" data-id="element-4869">
                {/* Skills */}
                <div data-id="element-4870">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4871">
                    <LightbulbIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4872" />
                    Skills You'll Gain
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-4873">
                    {program.skills.map((skill, index) => <SkillBadge key={index} {...skill} data-id="element-4874" />)}
                  </div>
                </div>
                {/* Topics */}
                {program.topics && program.topics.length > 0 && <div data-id="element-4875">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4876">
                      <BookOpenIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4877" />
                      Topics Covered
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-6" data-id="element-4878">
                      {program.topics.map((topic, index) => <TopicBadge key={index} name={topic} data-id="element-4879" />)}
                    </div>
                  </div>}
                {/* Labels */}
                {program.labels && program.labels.length > 0 && <div data-id="element-4880">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center" data-id="element-4881">
                      <TagIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4882" />
                      Program Highlights
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-4883">
                      {program.labels.map((label, index) => <LabelBadge key={index} name={label} data-id="element-4884" />)}
                    </div>
                  </div>}
              </div>
            </section>
            {/* Program Structure */}
            <section id="structure" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4885">
              <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-4886">
                Program Structure
              </h2>
              <p className="text-gray-700 mb-6" data-id="element-4887">
                The curriculum is structured to provide a comprehensive
                education with both core and elective courses:
              </p>
              <ProgramStructureAccordion structure={program.programStructure} data-id="element-4888" />
            </section>
            {/* FAQs */}
            {program.faqs && program.faqs.length > 0 && <section id="faqs" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4889">
                <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-4890">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-1" data-id="element-4891">
                  {program.faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-4892" />)}
                </div>
              </section>}
          </div>
          {/* Sidebar */}
          <div className="space-y-8" data-id="element-4893">
            {/* Tuition & Fees */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4894">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center" data-id="element-4895">
                <DollarSignIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4896" />
                Tuition & Fees
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4" data-id="element-4897">
                <div className="flex items-center justify-between" data-id="element-4898">
                  <div data-id="element-4899">
                    <p className="text-gray-600 text-sm" data-id="element-4900">Total Program Cost</p>
                    <p className="text-xl font-semibold text-gray-900" data-id="element-4901">
                      AED {program.tuition}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500" data-id="element-4902">
                * Additional fees may apply for textbooks, materials, and
                student services.
              </p>
            </div>
            {/* Admission Requirements */}
            <div id="admission" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4903">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center" data-id="element-4904">
                <CheckCircleIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4905" />
                Admission Requirements
              </h3>
              <ul className="space-y-3 mb-6" data-id="element-4906">
                {program.qualifications.map((qualification, index) => <li key={index} className="flex items-start" data-id="element-4907">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-4908" />
                    <span className="text-gray-700" data-id="element-4909">{qualification}</span>
                  </li>)}
              </ul>
              {/* Required Documents */}
              {program.requiredDocuments && program.requiredDocuments.length > 0 && <div data-id="element-4910">
                    <h4 className="font-medium text-gray-800 mb-2" data-id="element-4911">
                      Required Documents
                    </h4>
                    <ul className="space-y-2" data-id="element-4912">
                      {program.requiredDocuments.map((doc, index) => <li key={index} className="flex items-start" data-id="element-4913">
                          <FileTextIcon className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-4914" />
                          <span className="text-sm text-gray-600" data-id="element-4915">{doc}</span>
                        </li>)}
                    </ul>
                  </div>}
            </div>
            {/* Key Dates */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4916">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center" data-id="element-4917">
                <CalendarIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4918" />
                Key Dates
              </h3>
              <div className="space-y-4" data-id="element-4919">
                <div className="flex items-start" data-id="element-4920">
                  <div className="bg-primary-50 p-2 rounded-lg mr-3" data-id="element-4921">
                    <CalendarIcon className="w-5 h-5 text-primary-500" data-id="element-4922" />
                  </div>
                  <div data-id="element-4923">
                    <p className="font-medium text-gray-800" data-id="element-4924">
                      Application Deadline
                    </p>
                    <p className="text-gray-600" data-id="element-4925">
                      {program.applicationDeadline}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4926">
                  <div className="bg-primary-50 p-2 rounded-lg mr-3" data-id="element-4927">
                    <CalendarIcon className="w-5 h-5 text-primary-500" data-id="element-4928" />
                  </div>
                  <div data-id="element-4929">
                    <p className="font-medium text-gray-800" data-id="element-4930">
                      Program Start Date
                    </p>
                    <p className="text-gray-600" data-id="element-4931">{program.nextIntake}</p>
                  </div>
                </div>
                {program.intakePeriods && program.intakePeriods.length > 0 && <div className="flex items-start" data-id="element-4932">
                    <div className="bg-primary-50 p-2 rounded-lg mr-3" data-id="element-4933">
                      <CalendarIcon className="w-5 h-5 text-primary-500" data-id="element-4934" />
                    </div>
                    <div data-id="element-4935">
                      <p className="font-medium text-gray-800" data-id="element-4936">
                        Future Intakes
                      </p>
                      <div className="space-y-1 mt-1" data-id="element-4937">
                        {program.intakePeriods.map((intake, index) => <p key={index} className="text-gray-600" data-id="element-4938">
                            {intake}
                          </p>)}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
            {/* University Information */}
            <div id="university" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4939">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center" data-id="element-4940">
                <BuildingIcon className="w-5 h-5 text-primary-500 mr-2" data-id="element-4941" />
                About the University
              </h3>
              <div className="flex items-center mb-4" data-id="element-4942">
                {program.universityLogo ? <img src={program.universityLogo} alt={program.university} className="h-16 w-16 object-contain mr-4" data-id="element-4943" /> : <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mr-4" data-id="element-4944">
                    <BuildingIcon className="w-8 h-8 text-gray-400" data-id="element-4945" />
                  </div>}
                <div data-id="element-4946">
                  <h4 className="font-medium text-gray-800" data-id="element-4947">
                    {program.university}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-4948">
                    {program.location || program.campusLocation}
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-4949">
                {program.universityDescription}
              </p>
              <a href={program.universityWebsite} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium" data-id="element-4950">
                Visit University Website
                <ExternalLinkIcon className="w-4 h-4 ml-1" data-id="element-4951" />
              </a>
            </div>
            {/* Apply Now (Desktop Sticky) */}
            <div className="hidden md:block sticky top-6" data-id="element-4952">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6" data-id="element-4953">
                <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4954">
                  Ready to Apply?
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-4955">
                  Complete your application for the {program.name} program. The
                  application process takes approximately 20-30 minutes.
                </p>
                <button onClick={handleApply} className="w-full bg-primary-500 text-white px-4 py-3 rounded-md font-medium hover:bg-primary-600 transition-colors mb-3" data-id="element-4956">
                  Apply Now
                </button>
                <div className="flex gap-3" data-id="element-4957">
                  <button onClick={toggleSave} className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center
                      ${isSaved ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-4958">
                    <HeartIcon className={`w-4 h-4 mr-2 ${isSaved ? 'fill-red-500' : ''}`} data-id="element-4959" />
                    {isSaved ? 'Saved' : 'Save'}
                  </button>
                  <button onClick={handleShare} className="flex-1 bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center" data-id="element-4960">
                    <ShareIcon className="w-4 h-4 mr-2" data-id="element-4961" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default StudentProgramDetail;